import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Avatar, Stack, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Card, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchAllGroups, fetchGroupById, deleteGroup as deleteGroupAction, removeMemberFromGroup, setGroupLeader as setGroupLeaderAction } from '../../controllers/groupManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EditGroupInline from './EditGroupInline';
import CloseIcon from '@mui/icons-material/Close';
import CreateGroupDialog from './CreateGroupDialog';
import AddMemberBar from './AddMemberBar';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Skeleton from 'react-loading-skeleton';

const GroupsList = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [details, setDetails] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', description: '' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [groupToDeleteName, setGroupToDeleteName] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const data = await fetchAllGroups();
                if (mounted) {
                    setRows(data || []);
                    // Prefetch details for all groups to show member avatars
                    if (data && data.length > 0) {
                        const detailPromises = data.map(async (group) => {
                            try {
                                const d = await fetchGroupById(group.id);
                                return { id: group.id, data: d };
                            } catch (e) {
                                return { id: group.id, data: null };
                            }
                        });
                        const detailResults = await Promise.all(detailPromises);
                        const detailsMap = {};
                        detailResults.forEach(({ id, data }) => {
                            if (data) detailsMap[id] = data;
                        });
                        setDetails(detailsMap);
                    }
                }
            } catch (e) {
                if (mounted) setError(e?.message || 'Gruplar alınamadı');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Ocakbaşı Grupları</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => setCreateOpen(true)}
                >
                    Grup ekle
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2.5,
                    }}
                >
                    {loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <Card
                                key={`skeleton-${index}`}
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #f1f1f1',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                    borderRadius: '8px',
                                }}
                            >
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton width={150} height={24} />
                                            <Box sx={{ mt: 0.5, minHeight: '45px' }}>
                                                <Skeleton width="100%" height={20} count={1} />
                                            </Box>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <Skeleton width={32} height={32} borderRadius={6} />
                                            <Skeleton width={32} height={32} borderRadius={6} />
                                        </Stack>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" spacing={-1.5} alignItems="center">
                                            <Skeleton width={38} height={38} borderRadius="50%" />
                                            <Skeleton width={38} height={38} borderRadius="50%" />
                                            <Skeleton width={38} height={38} borderRadius="50%" />
                                        </Stack>
                                        <Skeleton width={60} height={24} borderRadius={12} />
                                    </Stack>
                                </Stack>
                            </Card>
                        ))
                    ) : rows.map((g) => {
                        const info = details[g.id];
                        const members = info?.members || [];

                        // Üyeler ve partnerlerini birleştir (her üye için kendisi + partneri varsa partneri)
                        const displayAvatars = [];
                        let displayedCount = 0;
                        const maxDisplay = 5;

                        for (const m of members) {
                            if (displayedCount >= maxDisplay) break;

                            // Üyenin kendisi
                            displayAvatars.push({
                                id: m.id,
                                profilePicture: m.profilePicture,
                                fullName: m.fullName,
                                isPartner: false,
                            });
                            displayedCount++;

                            // Partner varsa ekle
                            if (displayedCount < maxDisplay) {
                                // Önce member objesi içinde partner bilgisi var mı kontrol et (subMemberFullName/subMemberProfilePicture)
                                if (m.subMemberFullName && m.subMemberProfilePicture) {
                                    displayAvatars.push({
                                        id: m.subMemberId || `partner-${m.id}`,
                                        profilePicture: m.subMemberProfilePicture,
                                        fullName: m.subMemberFullName,
                                        isPartner: true,
                                        parentId: m.id,
                                    });
                                    displayedCount++;
                                } else if (m.subMembers && m.subMembers.length > 0) {
                                    // İlk subMember'ı partner olarak kabul et
                                    const partner = m.subMembers[0];
                                    displayAvatars.push({
                                        id: partner.id,
                                        profilePicture: partner.profilePicture,
                                        fullName: partner.fullName,
                                        isPartner: true,
                                        parentId: m.id,
                                    });
                                    displayedCount++;
                                }
                            }
                        }

                        return (
                            <Card
                                key={g.id}
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #f1f1f1',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: '#0E4D94',
                                        boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                                    },
                                }}
                                onClick={async () => {
                                    if (expandedId === g.id) {
                                        setExpandedId(null);
                                        return;
                                    }
                                    setExpandedId(g.id);
                                    if (!details[g.id]) {
                                        try {
                                            const d = await fetchGroupById(g.id);
                                            setDetails((prev) => ({ ...prev, [g.id]: d }));
                                        } catch (e) { }
                                    }
                                }}
                            >
                                <Stack spacing={2}>
                                    {/* Header: Başlık ve Butonlar */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1, minHeight: '60px' }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '18px',
                                                    fontWeight: 600,
                                                    color: '#444444',
                                                    mb: 0.5,
                                                }}
                                            >
                                                {g.name}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    minHeight: '50px',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                {g.description ? (
                                                    <Typography
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: '#a3a3a3',
                                                            lineHeight: 1.4,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {g.description}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{ height: '60px' }} />
                                                )}
                                            </Box>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Düzenle">
                                                <IconButton
                                                    size="small"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        setEditingId(g.id);
                                                        setEditForm({ name: g.name || '', description: g.description || '' });
                                                        if (!details[g.id]) {
                                                            try {
                                                                const d = await fetchGroupById(g.id);
                                                                setDetails((prev) => ({ ...prev, [g.id]: d }));
                                                                // Detaylar yüklendikten sonra editForm'u güncelle
                                                                setEditForm({ name: d.name || '', description: d.description || '' });
                                                            } catch (e) { }
                                                        }
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#E7EDF4',
                                                        borderRadius: '6px',
                                                        width: 32,
                                                        height: 32,
                                                        '&:hover': {
                                                            backgroundColor: '#CFDBEA',
                                                        },
                                                        '& svg': {
                                                            fontSize: '16px',
                                                            color: '#0E4D94',
                                                        },
                                                    }}
                                                >
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Sil">
                                                <IconButton
                                                    size="small"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        setGroupToDelete(g.id);
                                                        setGroupToDeleteName(g.name || '');
                                                        if (!details[g.id]) {
                                                            try {
                                                                const d = await fetchGroupById(g.id);
                                                                setDetails((prev) => ({ ...prev, [g.id]: d }));
                                                                setGroupToDeleteName(d.name || '');
                                                            } catch (e) { }
                                                        }
                                                        setConfirmOpen(true);
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#FFEEF3',
                                                        borderRadius: '6px',
                                                        width: 32,
                                                        height: 32,
                                                        '&:hover': {
                                                            backgroundColor: '#FFD6E0',
                                                        },
                                                        '& svg': {
                                                            fontSize: '16px',
                                                            color: '#F8285A',
                                                        },
                                                    }}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>

                                    {/* Üye ve Partner Avatar'ları */}
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        {displayAvatars.length > 0 ? (
                                            <Stack direction="row" spacing={-1.5} alignItems="center">
                                                {displayAvatars.map((avatar, index) => (
                                                    <Avatar
                                                        key={avatar.id}
                                                        src={avatar.profilePicture ? resolveGroupsAssetUrl(avatar.profilePicture) : undefined}
                                                        alt={avatar.fullName}
                                                        sx={{
                                                            width: 38,
                                                            height: 38,
                                                            border: '2.5px solid #ffffff',
                                                            cursor: 'pointer',
                                                            zIndex: displayAvatars.length - index,
                                                            backgroundColor: avatar.profilePicture ? 'transparent' : '#E7EDF4',
                                                            '&:hover': {
                                                                zIndex: 10,
                                                                transform: 'scale(1.1)',
                                                            },
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!avatar.isPartner) {
                                                                navigate(`/members/${avatar.id}`);
                                                            } else if (avatar.parentId) {
                                                                navigate(`/members/${avatar.parentId}`);
                                                            }
                                                        }}
                                                    >
                                                        {!avatar.profilePicture && (
                                                            <Typography sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 600 }}>
                                                                {avatar.fullName?.charAt(0) || '?'}
                                                            </Typography>
                                                        )}
                                                    </Avatar>
                                                ))}
                                                {(() => {
                                                    // Toplam gösterilebilecek avatar sayısını hesapla (her üye + partneri)
                                                    let totalPossibleAvatars = 0;
                                                    for (const m of members) {
                                                        totalPossibleAvatars++; // Üye kendisi
                                                        if (m.subMemberFullName && m.subMemberProfilePicture) {
                                                            totalPossibleAvatars++; // Partner (subMemberFullName/subMemberProfilePicture)
                                                        } else if (m.subMembers && m.subMembers.length > 0) {
                                                            totalPossibleAvatars++; // Partner (subMembers array'inden)
                                                        }
                                                    }
                                                    const remaining = totalPossibleAvatars - displayAvatars.length;
                                                    return remaining > 0 ? (
                                                        <Avatar
                                                            sx={{
                                                                width: 38,
                                                                height: 38,
                                                                border: '2.5px solid #ffffff',
                                                                backgroundColor: '#E7EDF4',
                                                                color: '#0E4D94',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            +{remaining}
                                                        </Avatar>
                                                    ) : null;
                                                })()}
                                            </Stack>
                                        ) : (
                                            <Box />
                                        )}
                                        {/* Toplam Üye Sayısı Badge */}
                                        <Chip
                                            label={`${(() => {
                                                // Toplam sayıyı hesapla (her üye + partneri varsa partneri)
                                                let totalCount = 0;
                                                for (const m of members) {
                                                    totalCount++; // Üye kendisi
                                                    // Partner varsa ekle
                                                    if (m.subMemberFullName && m.subMemberProfilePicture) {
                                                        totalCount++; // Partner (subMemberFullName/subMemberProfilePicture)
                                                    } else if (m.subMembers && m.subMembers.length > 0) {
                                                        totalCount++; // Partner (subMembers array'inden)
                                                    }
                                                }
                                                return totalCount;
                                            })()} üye`}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#E7EDF4',
                                                color: '#0E4D94',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                height: '24px',
                                                '& .MuiChip-label': {
                                                    px: 1,
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                            </Card>
                        );
                    })}
                </Box>
            )}

            {/* Detay Modal/Dialog - Accordion yerine modal kullanabiliriz */}
            {expandedId && details[expandedId] && (
                <Dialog
                    open={!!expandedId}
                    onClose={() => setExpandedId(null)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle
                        sx={{
                            fontSize: '18px',
                            fontWeight: 500,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pr: 3,
                        }}
                    >
                        {details[expandedId].name}
                        <IconButton
                            onClick={() => setExpandedId(null)}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        <Stack spacing={2.5}>
                            {details[expandedId].description && (
                                <Typography variant="body2" sx={{ fontSize: '16px', color: '#a3a3a3' }}>
                                    {details[expandedId].description}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                                    gap: 2,
                                }}
                            >
                                {details[expandedId].members?.map((m) => {
                                    const isLeader = details[expandedId].leaderId === m.id;
                                    // Partner bilgisini kontrol et
                                    const partner = m.subMemberFullName && m.subMemberProfilePicture
                                        ? { fullName: m.subMemberFullName, profilePicture: m.subMemberProfilePicture }
                                        : (m.subMembers && m.subMembers.length > 0 ? m.subMembers[0] : null);

                                    return (
                                        <Card
                                            key={m.id}
                                            sx={{
                                                p: 2,
                                                border: '1px solid #f1f1f1',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                                borderRadius: '8px',
                                                position: 'relative',
                                                '&:hover': {
                                                    borderColor: '#0E4D94',
                                                    boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                                                },
                                            }}
                                        >
                                            {/* Avatar'lar ve İsimler */}
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                {/* Avatar'lar */}
                                                <Stack direction="row" spacing={0.3} alignItems="center">
                                                    <Avatar
                                                        src={m.profilePicture ? resolveGroupsAssetUrl(m.profilePicture) : undefined}
                                                        alt={m.fullName}
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            cursor: 'pointer',
                                                            border: '2px solid #e6e6e6',
                                                        }}
                                                        onClick={() => navigate(`/members/${m.id}`)}
                                                    >
                                                        {!m.profilePicture && (
                                                            <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                                                                {m.fullName?.charAt(0) || '?'}
                                                            </Typography>
                                                        )}
                                                    </Avatar>
                                                    {partner && (
                                                        <Avatar
                                                            src={partner.profilePicture ? resolveGroupsAssetUrl(partner.profilePicture) : undefined}
                                                            alt={partner.fullName}
                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                cursor: 'pointer',
                                                                border: '2px solid #e6e6e6',
                                                            }}
                                                            onClick={() => navigate(`/members/${m.id}`)}
                                                        >
                                                            {!partner.profilePicture && (
                                                                <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                                                                    {partner.fullName?.charAt(0) || '?'}
                                                                </Typography>
                                                            )}
                                                        </Avatar>
                                                    )}
                                                </Stack>

                                                {/* İsimler */}
                                                <Stack spacing={0.3} sx={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate(`/members/${m.id}`)}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                                                            {m.fullName}
                                                        </Typography>
                                                        {isLeader && (
                                                            <Tooltip title="Grup Lideri">
                                                                <StarIcon sx={{ color: '#FFA500', fontSize: 16 }} />
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                    {partner && (
                                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                                                            {partner.fullName}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Stack>
                    </DialogContent>
                </Dialog>
            )}

            {/* Düzenleme Modalı - Ayrı Modal */}
            {editingId && details[editingId] && (
                <Dialog
                    open={!!editingId}
                    onClose={() => setEditingId(null)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle
                        sx={{
                            fontSize: '18px',
                            fontWeight: 500,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pr: 3,
                        }}
                    >
                        Grubu Düzenle
                        <IconButton
                            onClick={() => setEditingId(null)}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ py: 2, px: 3 }}>
                        <EditGroupInline
                            group={details[editingId]}
                            onNameChange={(name) => setEditForm((prev) => ({ ...prev, name }))}
                            onDescriptionChange={(description) => setEditForm((prev) => ({ ...prev, description }))}
                            onMemberAdded={async () => {
                                try {
                                    const d = await fetchGroupById(editingId);
                                    setDetails((prev) => ({ ...prev, [editingId]: d }));
                                } catch (e) { }
                            }}
                            onLeaderChanged={async (memberId) => {
                                try {
                                    await setGroupLeaderAction({ groupId: editingId, memberId });
                                    const updated = await fetchGroupById(editingId);
                                    setDetails((prev) => ({ ...prev, [editingId]: updated }));
                                    toast.success('Grup lideri güncellendi');
                                } catch (err) {
                                    toast.error(err?.message || 'Lider belirlenemedi');
                                }
                            }}
                            onRemoveMemberClick={(member) => {
                                setMemberToRemove({ groupId: editingId, member, groupName: details[editingId].name });
                                setConfirmRemoveOpen(true);
                            }}
                            navigate={navigate}
                        />
                    </DialogContent>
                    <DialogActions sx={{ py: 2, px: 3 }}>
                        <Button
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                            sx={{
                                textTransform: 'none',
                                fontSize: '13px',
                                color: '#666666',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            İptal
                        </Button>
                        <Button
                            variant="contained"
                            onClick={async () => {
                                const hasChanges = editForm.name !== details[editingId]?.name || editForm.description !== details[editingId]?.description;
                                if (!hasChanges) return;
                                setSaving(true);
                                try {
                                    const updated = await (await import('../../controllers/groupManagementController')).updateGroup({
                                        id: editingId,
                                        name: editForm.name || details[editingId]?.name || '',
                                        description: editForm.description || details[editingId]?.description || ''
                                    });
                                    setDetails((prev) => ({ ...prev, [editingId]: { ...details[editingId], name: updated.name, description: updated.description } }));
                                    setRows((prev) => prev.map((g) => (g.id === editingId ? { ...g, name: updated.name, description: updated.description } : g)));
                                    toast.success('Grup başarıyla güncellendi');
                                    setEditingId(null);
                                    setEditForm({ name: '', description: '' });
                                } catch (e) {
                                    toast.error(e?.message || 'Grup güncellenemedi');
                                }
                                finally {
                                    setSaving(false);
                                }
                            }}
                            disabled={saving || (editForm.name === details[editingId]?.name && editForm.description === details[editingId]?.description)}
                            sx={{
                                textTransform: 'none',
                                fontSize: '13px',
                                backgroundColor: '#0E4D94',
                                borderRadius: '5px',
                                px: 2,
                                '&:hover': {
                                    backgroundColor: '#0a3d7a',
                                },
                                '&:disabled': {
                                    backgroundColor: '#e6e6e6',
                                    color: '#a3a3a3',
                                },
                            }}
                        >
                            Kaydet
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setGroupToDelete(null);
                    setGroupToDeleteName('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Grubu Sil
                    <IconButton
                        onClick={() => {
                            setConfirmOpen(false);
                            setGroupToDelete(null);
                            setGroupToDeleteName('');
                        }}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
                        {groupToDeleteName ? `${groupToDeleteName} grubunu silmek istediğinize emin misiniz?` : 'Bu grubu silmek istediğinize emin misiniz?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => {
                            setConfirmOpen(false);
                            setGroupToDelete(null);
                            setGroupToDeleteName('');
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            if (!groupToDelete) return;
                            try {
                                await deleteGroupAction(groupToDelete);
                                const groupName = groupToDeleteName || 'Grup';
                                setConfirmOpen(false);
                                setGroupToDelete(null);
                                setGroupToDeleteName('');
                                setExpandedId(null);
                                // reload groups
                                setLoading(true);
                                const data = await fetchAllGroups();
                                setRows(data || []);
                                // Prefetch details for all groups to show member avatars
                                if (data && data.length > 0) {
                                    const detailPromises = data.map(async (group) => {
                                        try {
                                            const d = await fetchGroupById(group.id);
                                            return { id: group.id, data: d };
                                        } catch (e) {
                                            return { id: group.id, data: null };
                                        }
                                    });
                                    const detailResults = await Promise.all(detailPromises);
                                    const detailsMap = {};
                                    detailResults.forEach(({ id, data }) => {
                                        if (data) detailsMap[id] = data;
                                    });
                                    setDetails(detailsMap);
                                } else {
                                    setDetails({});
                                }
                                toast.success(`${groupName} başarıyla silindi`);
                            } catch (e) {
                                toast.error(e?.message || 'Grup silinemedi');
                            } finally {
                                setLoading(false);
                            }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#F8285A',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#d91e47',
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)}>
                <DialogTitle>Üyeyi Gruptan Kaldır</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {memberToRemove ? `${memberToRemove.member.fullName} kullanıcısını ${memberToRemove.groupName} grubundan çıkarmak istediğinize emin misiniz?` : 'Seçili üyeyi gruptan çıkarmak istediğinize emin misiniz?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRemoveOpen(false)}>İptal</Button>
                    <Button color="error" variant="contained" onClick={async () => {
                        if (!memberToRemove) return;
                        try {
                            await removeMemberFromGroup({ groupId: memberToRemove.groupId, memberId: memberToRemove.member.id });
                            const d = await fetchGroupById(memberToRemove.groupId);
                            setDetails((prev) => ({ ...prev, [memberToRemove.groupId]: d }));
                            toast.success('Üye gruptan kaldırıldı');
                        } catch (e) {
                            toast.error(e?.message || 'Üye kaldırılamadı');
                        }
                        finally {
                            setConfirmRemoveOpen(false);
                            setMemberToRemove(null);
                        }
                    }}>Kaldır</Button>
                </DialogActions>
            </Dialog>

            <CreateGroupDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={async () => {
                    setLoading(true);
                    const data = await fetchAllGroups();
                    setRows(data || []);
                    setLoading(false);
                }}
            />
        </Box>
    );
};

export default GroupsList;


